import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { Icons } from '@ohif/ui-next';

export interface VirtualizedDropdownOption {
  id: number;
  label: string;
  /** Optional second line (e.g. specialty / modality access) under the primary label */
  subtitle?: string;
}

function getOptionButtonClassName(
  isLight: boolean,
  isSelected: boolean,
  isHighlighted: boolean
): string {
  if (isLight) {
    if (isSelected) {
      return 'flex min-h-[40px] w-full items-start justify-start gap-2 px-4 py-2.5 text-left text-[14px] leading-snug transition-colors cursor-default bg-white text-[#737373]';
    }
    if (isHighlighted) {
      return 'flex min-h-[40px] w-full items-start justify-start gap-2 px-4 py-2.5 text-left text-[14px] leading-snug transition-colors bg-[#f3f3f5] text-[#18181b]';
    }
    return 'flex min-h-[40px] w-full items-start justify-start gap-2 px-4 py-2.5 text-left text-[14px] leading-snug transition-colors text-[#18181b] hover:bg-[#f9fafb]';
  }
  if (isSelected) {
    return 'flex min-h-[40px] w-full items-start justify-start gap-2 px-4 py-2.5 text-left text-sm transition-colors cursor-default bg-gray-900 text-gray-500';
  }
  if (isHighlighted) {
    return 'flex min-h-[40px] w-full items-start justify-start gap-2 px-4 py-2.5 text-left text-sm transition-colors text-primary-light bg-gray-800';
  }
  return 'flex min-h-[40px] w-full items-start justify-start gap-2 px-4 py-2.5 text-left text-sm transition-colors text-white hover:bg-gray-800';
}

function nextHighlightedIndex(
  prev: number,
  direction: 'up' | 'down',
  totalOptions: number,
  onlyKeyboardSyncsListScroll: boolean,
  shouldSyncScrollToHighlightRef: React.MutableRefObject<boolean>
): number {
  if (direction === 'down') {
    if (prev < totalOptions - 1) {
      if (onlyKeyboardSyncsListScroll) {
        shouldSyncScrollToHighlightRef.current = true;
      }
      return prev + 1;
    }
  } else if (prev > 0) {
    if (onlyKeyboardSyncsListScroll) {
      shouldSyncScrollToHighlightRef.current = true;
    }
    return prev - 1;
  }
  if (onlyKeyboardSyncsListScroll) {
    shouldSyncScrollToHighlightRef.current = false;
  }
  return prev;
}

function DropdownOptionRow({
  option,
  index,
  isSelected,
  isHighlighted,
  isLight,
  onlyKeyboardSyncsListScroll,
  shouldSyncScrollToHighlightRef,
  richSubtitlePrimaryClassName,
  richSubtitleSecondaryClassName,
  onSelect,
  onHighlight,
}: Readonly<{
  option: { id: number | null; label: string; subtitle?: string };
  index: number;
  isSelected: boolean;
  isHighlighted: boolean;
  isLight: boolean;
  onlyKeyboardSyncsListScroll: boolean;
  shouldSyncScrollToHighlightRef: React.MutableRefObject<boolean>;
  richSubtitlePrimaryClassName?: string;
  richSubtitleSecondaryClassName?: string;
  onSelect: (id: number | null, label: string) => void;
  onHighlight: (index: number) => void;
}>) {
  const hasSubtitle = option.subtitle != null && String(option.subtitle).trim() !== '';
  return (
    <button
      type="button"
      aria-disabled={isSelected}
      onMouseDown={e => {
        e.preventDefault();
      }}
      onClick={() => {
        if (isSelected) {
          return;
        }
        onSelect(option.id, option.label);
      }}
      onMouseEnter={() => {
        if (onlyKeyboardSyncsListScroll) {
          shouldSyncScrollToHighlightRef.current = false;
        }
        onHighlight(index);
      }}
      className={getOptionButtonClassName(isLight, isSelected, isHighlighted)}
    >
      {hasSubtitle ? (
        <span className="flex w-full flex-col gap-0.5 whitespace-normal break-words text-left">
          <span
            className={
              richSubtitlePrimaryClassName ??
              (isLight
                ? 'text-sm font-medium leading-tight text-[#333333]'
                : 'font-semibold text-white')
            }
          >
            {option.label}
          </span>
          <span
            className={
              richSubtitleSecondaryClassName ??
              (isLight
                ? 'text-[13px] font-normal text-[#666666]'
                : 'text-[13px] font-normal text-gray-400')
            }
          >
            {String(option.subtitle).trim()}
          </span>
        </span>
      ) : (
        <span className="w-full whitespace-normal break-words text-left">{option.label}</span>
      )}
    </button>
  );
}

interface VirtualizedDropdownProps {
  value?: number | string | null;
  onChange: (value: number | null, label: string) => void;
  options: VirtualizedDropdownOption[];
  placeholder?: string;
  disabled?: boolean;
  isLoading?: boolean;
  className?: string;
  noneLabel?: string;
  showNone?: boolean;
  searchPlaceholder?: string;
  emptyMessage?: string;
  loadingMessage?: string;
  containerClassName?: string;
  showSearch?: boolean;
  searchValue?: string;
  onSearchChange?: (search: string) => void;
  /** Light surface (Figma admin filters); default matches legacy dark trigger */
  variant?: 'dark' | 'light';
  /**
   * When the selected option label is one of these strings, use muted trigger text (#737373)
   * like an empty/placeholder state (e.g. "All Statuses" vs a concrete selection).
   */
  mutedDisplayLabels?: string[];
  /** When false, show full label in the trigger (wrap lines) instead of ellipsis. Default true. */
  truncateTriggerLabel?: boolean;
  /**
   * When the selected option has a subtitle, overrides the primary line typography (e.g. modal
   * assign flow: softer label than default semibold).
   */
  richSubtitlePrimaryClassName?: string;
  /** When the selected option has a subtitle, overrides the secondary line (e.g. modalities). */
  richSubtitleSecondaryClassName?: string;
  /**
   * When true, the options list only auto-scrolls to follow the highlight via ArrowUp/ArrowDown,
   * not when the mouse moves over rows. Default true (pointer hover no longer jerks the list).
   */
  onlyKeyboardSyncsListScroll?: boolean;
  /** Multiplier for mouse-wheel scrolling speed inside dropdown options. Default 0.22 */
  wheelScrollMultiplier?: number;
  /** Max pixels to scroll per wheel event (dampens large trackpad deltas). Default 14 */
  wheelScrollMaxDeltaPx?: number;
  /**
   * When true, wheel/trackpad uses native browser scrolling on the options list (same pattern as
   * FormSelectVirtualized on User Management / facility-style filters). Ignores wheel multiplier/cap.
   * Default false (custom damped wheel handler).
   */
  nativeOptionsWheelScroll?: boolean;
  /** Accessible name for the trigger button (e.g. filter role in a toolbar). */
  ariaLabel?: string;
}

const OPTION_HEIGHT = 40; // Height of each option
const VISIBLE_OPTIONS = 4; // Show 4 options at once
/** Extra pixels past the list bottom before we scroll (subpixel / border tolerance). */
const LIST_SCROLL_EDGE_SLACK_PX = 6;

/**
 * Scroll a scrollable list so a row stays visible. Uses viewport rects: `offsetTop` is wrong here
 * because option `offsetParent` is often the positioned dropdown panel (above the list), not the
 * scroll container — that made early rows look "below" the fold and triggered spurious scroll.
 */
function adjustScrollContainerToKeepChildVisible(
  container: HTMLDivElement,
  child: HTMLElement,
  bottomSlackPx: number
): void {
  const c = container.getBoundingClientRect();
  const o = child.getBoundingClientRect();

  if (o.top < c.top) {
    container.scrollTop += o.top - c.top;
    return;
  }
  const excessBelow = o.bottom - c.bottom - bottomSlackPx;
  if (excessBelow > 0) {
    container.scrollTop += excessBelow;
  }
}

function clearDropdownSearch(
  onSearchChange: ((search: string) => void) | undefined,
  setInternalSearchQuery: (value: string) => void
): void {
  if (onSearchChange) {
    onSearchChange('');
    return;
  }
  setInternalSearchQuery('');
}

function resetHighlightState(
  onlyKeyboardSyncsListScroll: boolean,
  shouldSyncScrollToHighlightRef: React.MutableRefObject<boolean>,
  setHighlightedIndex: (index: number) => void
): void {
  if (onlyKeyboardSyncsListScroll) {
    shouldSyncScrollToHighlightRef.current = false;
  }
  setHighlightedIndex(0);
}

function closeAndResetDropdown(params: {
  setIsOpen: (open: boolean) => void;
  onSearchChange?: (search: string) => void;
  setInternalSearchQuery: (value: string) => void;
  onlyKeyboardSyncsListScroll: boolean;
  shouldSyncScrollToHighlightRef: React.MutableRefObject<boolean>;
  setHighlightedIndex: (index: number) => void;
}): void {
  params.setIsOpen(false);
  clearDropdownSearch(params.onSearchChange, params.setInternalSearchQuery);
  resetHighlightState(
    params.onlyKeyboardSyncsListScroll,
    params.shouldSyncScrollToHighlightRef,
    params.setHighlightedIndex
  );
}

function resolveSelectedSubtitle(selectedOption: VirtualizedDropdownOption | undefined): string {
  if (selectedOption?.subtitle == null) {
    return '';
  }
  const trimmed = String(selectedOption.subtitle).trim();
  return trimmed === '' ? '' : trimmed;
}

function isLightTriggerMuted(params: {
  isLight: boolean;
  triggerRichSubtitle: boolean;
  noneSelectedShowsPlaceholder: boolean;
  displayValue: string;
  mutedDisplayLabels?: string[];
}): boolean {
  if (!params.isLight || params.triggerRichSubtitle || params.noneSelectedShowsPlaceholder) {
    return false;
  }
  if (!params.displayValue) {
    return true;
  }
  const labels = params.mutedDisplayLabels;
  return Boolean(labels != null && labels.length > 0 && labels.includes(params.displayValue));
}

function filterDropdownOptions(
  options: VirtualizedDropdownOption[],
  searchQuery: string,
  externalSearchValue: string | undefined
): VirtualizedDropdownOption[] {
  if (externalSearchValue !== undefined) {
    return options;
  }
  if (!searchQuery.trim()) {
    return options;
  }
  const query = searchQuery.toLowerCase();
  return options.filter(
    option => option.label.toLowerCase().includes(query) || option.id.toString().includes(query)
  );
}

type VirtualizedDropdownSelectedValue = number | string | null | undefined;

function isOptionValueSelected(
  optionId: number | null,
  value: VirtualizedDropdownSelectedValue
): boolean {
  if (optionId === null) {
    return value == null;
  }
  return value != null && (optionId === value || Number(optionId) === Number(value));
}

function selectHighlightedOption(params: {
  showNone: boolean;
  highlightedIndex: number;
  noneLabel: string;
  filteredOptions: VirtualizedDropdownOption[];
  onSelect: (id: number | null, label: string) => void;
}): void {
  if (params.showNone && params.highlightedIndex === 0) {
    params.onSelect(null, params.noneLabel);
    return;
  }
  const optionIndex = params.showNone ? params.highlightedIndex - 1 : params.highlightedIndex;
  const option = params.filteredOptions[optionIndex];
  if (option) {
    params.onSelect(option.id, option.label);
  }
}

function getTriggerButtonClassName(
  isLight: boolean,
  truncateTriggerLabel: boolean,
  className: string
): string {
  if (isLight) {
    if (truncateTriggerLabel) {
      return `relative flex h-9 w-full shrink-0 items-center rounded-[5.2px] border border-[#e5e7eb] bg-[#f9fafb] px-[21px] py-px pr-10 text-left text-[14px] leading-5 text-[#18181b] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)] disabled:cursor-not-allowed ${className}`;
    }
    return `relative flex h-auto min-h-[40px] w-full shrink-0 items-start gap-2 rounded-[5.2px] border border-[#e5e7eb] bg-[#f9fafb] px-[21px] py-2 pr-10 text-left text-[14px] leading-snug text-[#18181b] focus:outline-none focus:ring-1 focus:ring-[var(--accent-primary)] disabled:cursor-not-allowed ${className}`;
  }
  if (truncateTriggerLabel) {
    return `focus:border-primary-light relative flex w-full items-center rounded-lg border border-gray-600 bg-gray-900 px-4 py-2 pr-10 text-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${className}`;
  }
  return `focus:border-primary-light relative flex h-auto min-h-[40px] w-full items-start gap-2 rounded-lg border border-gray-600 bg-gray-900 px-4 py-2 pr-10 text-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 ${className}`;
}

function getPlainTriggerLabelClassName(
  isLight: boolean,
  lightTriggerMuted: boolean,
  truncateTriggerLabel: boolean,
  hasDisplayValue: boolean
): string {
  if (isLight) {
    const color = lightTriggerMuted ? 'text-[#737373]' : 'text-[#18181b]';
    if (truncateTriggerLabel) {
      return `min-w-0 flex-1 truncate text-left ${color}`;
    }
    return `min-w-0 flex-1 whitespace-normal break-words text-left ${color}`;
  }
  const color = hasDisplayValue ? 'text-white' : 'text-gray-500';
  if (truncateTriggerLabel) {
    return `min-w-0 flex-1 truncate text-left ${color}`;
  }
  return `min-w-0 flex-1 whitespace-normal break-words text-left ${color}`;
}

function getStatusMessageClassName(isLight: boolean): string {
  if (isLight) {
    return 'px-4 py-3 text-center text-[14px] text-[#737373]';
  }
  return 'px-4 py-3 text-center text-gray-400';
}

function syncScrollToHighlightedOption(
  optionsContainer: HTMLDivElement | null,
  highlightedIndex: number,
  onlyKeyboardSyncsListScroll: boolean,
  shouldSyncScrollToHighlightRef: React.MutableRefObject<boolean>
): void {
  if (!optionsContainer) {
    return;
  }
  if (onlyKeyboardSyncsListScroll) {
    if (!shouldSyncScrollToHighlightRef.current) {
      return;
    }
    shouldSyncScrollToHighlightRef.current = false;
  }

  const optionElement = optionsContainer.children[highlightedIndex] as HTMLElement;
  if (!optionElement || optionsContainer.clientHeight <= 0) {
    return;
  }
  adjustScrollContainerToKeepChildVisible(
    optionsContainer,
    optionElement,
    LIST_SCROLL_EDGE_SLACK_PX
  );
}

function DropdownTriggerLabel({
  triggerRichSubtitle,
  displayValue,
  selectedSubtitle,
  placeholder,
  isLight,
  lightTriggerMuted,
  truncateTriggerLabel,
  richSubtitlePrimaryClassName,
  richSubtitleSecondaryClassName,
}: Readonly<{
  triggerRichSubtitle: boolean;
  displayValue: string;
  selectedSubtitle: string;
  placeholder: string;
  isLight: boolean;
  lightTriggerMuted: boolean;
  truncateTriggerLabel: boolean;
  richSubtitlePrimaryClassName?: string;
  richSubtitleSecondaryClassName?: string;
}>) {
  if (triggerRichSubtitle) {
    return (
      <span className="flex min-w-0 flex-1 flex-col items-start gap-0.5 pr-1 text-left">
        <span
          className={
            richSubtitlePrimaryClassName ??
            (isLight
              ? 'w-full text-[15px] font-semibold leading-tight text-[#000000]'
              : 'w-full text-[15px] font-semibold leading-tight text-white')
          }
        >
          {displayValue}
        </span>
        <span
          className={
            richSubtitleSecondaryClassName ??
            (isLight
              ? 'w-full text-[13px] font-normal leading-snug text-[#666666]'
              : 'w-full text-[13px] font-normal leading-snug text-gray-400')
          }
        >
          {selectedSubtitle}
        </span>
      </span>
    );
  }

  return (
    <span
      className={getPlainTriggerLabelClassName(
        isLight,
        lightTriggerMuted,
        truncateTriggerLabel,
        Boolean(displayValue)
      )}
    >
      {displayValue || placeholder}
    </span>
  );
}

function DropdownOptionsList({
  isLoading,
  loadingMessage,
  emptyMessage,
  displayOptions,
  highlightedIndex,
  isLight,
  onlyKeyboardSyncsListScroll,
  shouldSyncScrollToHighlightRef,
  richSubtitlePrimaryClassName,
  richSubtitleSecondaryClassName,
  value,
  onSelect,
  onHighlight,
  optionsContainerRef,
  nativeOptionsWheelScroll,
  onWheel,
}: Readonly<{
  isLoading: boolean;
  loadingMessage: string;
  emptyMessage: string;
  displayOptions: Array<{ id: number | null; label: string; subtitle?: string }>;
  highlightedIndex: number;
  isLight: boolean;
  onlyKeyboardSyncsListScroll: boolean;
  shouldSyncScrollToHighlightRef: React.MutableRefObject<boolean>;
  richSubtitlePrimaryClassName?: string;
  richSubtitleSecondaryClassName?: string;
  value: number | string | null | undefined;
  onSelect: (id: number | null, label: string) => void;
  onHighlight: (index: number) => void;
  optionsContainerRef: React.RefObject<HTMLDivElement>;
  nativeOptionsWheelScroll: boolean;
  onWheel?: (e: React.WheelEvent<HTMLDivElement>) => void;
}>) {
  let listContent: React.ReactNode;
  if (isLoading) {
    listContent = <div className={getStatusMessageClassName(isLight)}>{loadingMessage}</div>;
  } else if (displayOptions.length === 0) {
    listContent = <div className={getStatusMessageClassName(isLight)}>{emptyMessage}</div>;
  } else {
    listContent = displayOptions.map((option, index) => (
      <DropdownOptionRow
        key={option.id === null ? 'none' : option.id}
        option={option}
        index={index}
        isSelected={isOptionValueSelected(option.id, value)}
        isHighlighted={index === highlightedIndex}
        isLight={isLight}
        onlyKeyboardSyncsListScroll={onlyKeyboardSyncsListScroll}
        shouldSyncScrollToHighlightRef={shouldSyncScrollToHighlightRef}
        richSubtitlePrimaryClassName={richSubtitlePrimaryClassName}
        richSubtitleSecondaryClassName={richSubtitleSecondaryClassName}
        onSelect={onSelect}
        onHighlight={onHighlight}
      />
    ));
  }

  const optionsListMaxHeight =
    Math.min(VISIBLE_OPTIONS, Math.max(displayOptions.length, 1)) * OPTION_HEIGHT;

  return (
    <div
      ref={optionsContainerRef}
      className="overflow-y-auto overflow-x-hidden"
      style={{
        maxHeight: `${optionsListMaxHeight}px`,
        ...(nativeOptionsWheelScroll ? { overscrollBehavior: 'contain' as const } : {}),
      }}
      onWheel={onWheel}
    >
      {listContent}
    </div>
  );
}

function DropdownMenuPanel({
  isLight,
  showSearch,
  searchInputRef,
  searchQuery,
  searchPlaceholder,
  onSearchChange,
  isLoading,
  loadingMessage,
  emptyMessage,
  displayOptions,
  highlightedIndex,
  onlyKeyboardSyncsListScroll,
  shouldSyncScrollToHighlightRef,
  richSubtitlePrimaryClassName,
  richSubtitleSecondaryClassName,
  value,
  onSelect,
  onHighlight,
  optionsContainerRef,
  nativeOptionsWheelScroll,
  onWheel,
}: Readonly<{
  isLight: boolean;
  showSearch: boolean;
  searchInputRef: React.RefObject<HTMLInputElement>;
  searchQuery: string;
  searchPlaceholder: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isLoading: boolean;
  loadingMessage: string;
  emptyMessage: string;
  displayOptions: Array<{ id: number | null; label: string; subtitle?: string }>;
  highlightedIndex: number;
  onlyKeyboardSyncsListScroll: boolean;
  shouldSyncScrollToHighlightRef: React.MutableRefObject<boolean>;
  richSubtitlePrimaryClassName?: string;
  richSubtitleSecondaryClassName?: string;
  value: number | string | null | undefined;
  onSelect: (id: number | null, label: string) => void;
  onHighlight: (index: number) => void;
  optionsContainerRef: React.RefObject<HTMLDivElement>;
  nativeOptionsWheelScroll: boolean;
  onWheel?: (e: React.WheelEvent<HTMLDivElement>) => void;
}>) {
  return (
    <div
      className={
        isLight
          ? 'absolute left-0 top-full z-50 mt-1 w-full rounded-[5.2px] border border-[#e5e7eb] bg-white shadow-md'
          : 'absolute left-0 top-full z-50 mt-1 w-full rounded-lg border border-gray-600 bg-gray-900 shadow-lg'
      }
    >
      {showSearch ? (
        <div className={isLight ? 'border-b border-[#e5e7eb] p-2' : 'border-b border-gray-700 p-2'}>
          <input
            ref={searchInputRef}
            type="text"
            value={searchQuery}
            onChange={onSearchChange}
            placeholder={searchPlaceholder}
            className={
              isLight
                ? 'focus:border-primary-light w-full rounded-md border border-[#e5e7eb] bg-[#f9fafb] px-3 py-2 text-[14px] text-[#18181b] placeholder-[#a1a1aa] focus:outline-none'
                : 'focus:border-primary-light w-full rounded-lg border border-gray-600 bg-gray-800 px-3 py-2 text-white placeholder-gray-500 focus:outline-none'
            }
            onKeyDown={e => {
              if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter') {
                e.preventDefault();
              }
            }}
          />
        </div>
      ) : null}
      <DropdownOptionsList
        isLoading={isLoading}
        loadingMessage={loadingMessage}
        emptyMessage={emptyMessage}
        displayOptions={displayOptions}
        highlightedIndex={highlightedIndex}
        isLight={isLight}
        onlyKeyboardSyncsListScroll={onlyKeyboardSyncsListScroll}
        shouldSyncScrollToHighlightRef={shouldSyncScrollToHighlightRef}
        richSubtitlePrimaryClassName={richSubtitlePrimaryClassName}
        richSubtitleSecondaryClassName={richSubtitleSecondaryClassName}
        value={value}
        onSelect={onSelect}
        onHighlight={onHighlight}
        optionsContainerRef={optionsContainerRef}
        nativeOptionsWheelScroll={nativeOptionsWheelScroll}
        onWheel={onWheel}
      />
    </div>
  );
}

const VirtualizedDropdown: React.FC<VirtualizedDropdownProps> = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  isLoading = false,
  className = '',
  noneLabel = 'All',
  showNone = true,
  searchPlaceholder = 'Search...',
  emptyMessage = 'No options found',
  loadingMessage = 'Loading...',
  containerClassName = '',
  showSearch = true,
  searchValue: externalSearchValue,
  onSearchChange,
  variant = 'dark',
  mutedDisplayLabels,
  truncateTriggerLabel = true,
  richSubtitlePrimaryClassName,
  richSubtitleSecondaryClassName,
  onlyKeyboardSyncsListScroll = true,
  wheelScrollMultiplier = 0.22,
  wheelScrollMaxDeltaPx = 14,
  nativeOptionsWheelScroll = false,
  ariaLabel,
}) => {
  const isLight = variant === 'light';
  const [isOpen, setIsOpen] = useState(false);
  const [internalSearchQuery, setInternalSearchQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  /** When onlyKeyboardSyncsListScroll: only scroll list for keyboard highlight moves, not pointer. */
  const shouldSyncScrollToHighlightRef = useRef(false);

  const searchQuery = externalSearchValue !== undefined ? externalSearchValue : internalSearchQuery;

  const filteredOptions = useMemo(
    () => filterDropdownOptions(options, searchQuery, externalSearchValue),
    [options, searchQuery, externalSearchValue]
  );

  const selectedOption = options.find(opt => opt.id === value);
  const displayValue = selectedOption ? selectedOption.label : '';
  const selectedSubtitle = resolveSelectedSubtitle(selectedOption);
  const triggerRichSubtitle = Boolean(selectedSubtitle);
  const noneSelectedShowsPlaceholder = Boolean(showNone && (value == null || value === ''));
  const lightTriggerMuted = isLightTriggerMuted({
    isLight,
    triggerRichSubtitle,
    noneSelectedShowsPlaceholder,
    displayValue,
    mutedDisplayLabels,
  });

  const resetClosedState = useCallback(() => {
    closeAndResetDropdown({
      setIsOpen,
      onSearchChange,
      setInternalSearchQuery,
      onlyKeyboardSyncsListScroll,
      shouldSyncScrollToHighlightRef,
      setHighlightedIndex,
    });
  }, [onSearchChange, onlyKeyboardSyncsListScroll]);

  const handleSelect = useCallback(
    (selectedValue: number | null, selectedLabel: string) => {
      if (!isOptionValueSelected(selectedValue, value)) {
        onChange(selectedValue, selectedLabel);
      }
      resetClosedState();
    },
    [value, onChange, resetClosedState]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        resetClosedState();
      }
    };

    if (!isOpen) {
      return undefined;
    }

    document.addEventListener('mousedown', handleClickOutside);
    if (showSearch) {
      setTimeout(() => {
        searchInputRef.current?.focus({ preventScroll: true });
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, showSearch, onSearchChange, onlyKeyboardSyncsListScroll, resetClosedState]);

  useEffect(() => {
    resetHighlightState(
      onlyKeyboardSyncsListScroll,
      shouldSyncScrollToHighlightRef,
      setHighlightedIndex
    );
  }, [filteredOptions.length, searchQuery, onlyKeyboardSyncsListScroll]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      const totalOptions = showNone ? filteredOptions.length + 1 : filteredOptions.length;

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(prev =>
          nextHighlightedIndex(
            prev,
            e.key === 'ArrowDown' ? 'down' : 'up',
            totalOptions,
            onlyKeyboardSyncsListScroll,
            shouldSyncScrollToHighlightRef
          )
        );
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        selectHighlightedOption({
          showNone,
          highlightedIndex,
          noneLabel,
          filteredOptions,
          onSelect: handleSelect,
        });
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        resetClosedState();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [
    isOpen,
    filteredOptions,
    highlightedIndex,
    showNone,
    noneLabel,
    onSearchChange,
    onChange,
    value,
    onlyKeyboardSyncsListScroll,
    handleSelect,
    resetClosedState,
  ]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    syncScrollToHighlightedOption(
      optionsContainerRef.current,
      highlightedIndex,
      onlyKeyboardSyncsListScroll,
      shouldSyncScrollToHighlightRef
    );
  }, [highlightedIndex, isOpen, onlyKeyboardSyncsListScroll]);

  const handleToggle = () => {
    if (disabled || isLoading) {
      return;
    }
    setIsOpen(!isOpen);
    if (!isOpen) {
      clearDropdownSearch(onSearchChange, setInternalSearchQuery);
      resetHighlightState(
        onlyKeyboardSyncsListScroll,
        shouldSyncScrollToHighlightRef,
        setHighlightedIndex
      );
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    if (onSearchChange) {
      onSearchChange(newValue);
    } else {
      setInternalSearchQuery(newValue);
    }
    resetHighlightState(
      onlyKeyboardSyncsListScroll,
      shouldSyncScrollToHighlightRef,
      setHighlightedIndex
    );
  };

  const displayOptions = useMemo(() => {
    const list: Array<{ id: number | null; label: string; subtitle?: string }> = [];
    if (showNone) {
      list.push({ id: null, label: noneLabel });
    }
    filteredOptions.forEach(option => {
      list.push({
        id: option.id,
        label: option.label,
        subtitle: option.subtitle,
      });
    });
    return list;
  }, [filteredOptions, showNone, noneLabel]);

  const handleOptionsWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (!optionsContainerRef.current) {
      return;
    }
    const container = optionsContainerRef.current;
    const maxScrollTop = Math.max(container.scrollHeight - container.clientHeight, 0);
    if (maxScrollTop <= 0) {
      return;
    }

    const scaled = e.deltaY * wheelScrollMultiplier;
    const damped = Math.sign(scaled) * Math.min(Math.abs(scaled), wheelScrollMaxDeltaPx);
    const nextScrollTop = Math.min(maxScrollTop, Math.max(0, container.scrollTop + damped));

    e.preventDefault();
    container.scrollTop = nextScrollTop;
  };

  return (
    <div
      ref={containerRef}
      className={['relative', containerClassName, isOpen ? 'virtualized-dropdown--open' : '']
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        onClick={handleToggle}
        disabled={disabled || isLoading}
        className={getTriggerButtonClassName(isLight, truncateTriggerLabel, className)}
      >
        <DropdownTriggerLabel
          triggerRichSubtitle={triggerRichSubtitle}
          displayValue={displayValue}
          selectedSubtitle={selectedSubtitle}
          placeholder={placeholder}
          isLight={isLight}
          lightTriggerMuted={lightTriggerMuted}
          truncateTriggerLabel={truncateTriggerLabel}
          richSubtitlePrimaryClassName={richSubtitlePrimaryClassName}
          richSubtitleSecondaryClassName={richSubtitleSecondaryClassName}
        />
        <Icons.ChevronDown
          className={`pointer-events-none absolute right-3 top-1/2 h-4 w-4 shrink-0 -translate-y-1/2 transition-transform ${isLight ? 'text-[#737373]' : ''} ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && !disabled ? (
        <DropdownMenuPanel
          isLight={isLight}
          showSearch={showSearch}
          searchInputRef={searchInputRef}
          searchQuery={searchQuery}
          searchPlaceholder={searchPlaceholder}
          onSearchChange={handleSearchChange}
          isLoading={isLoading}
          loadingMessage={loadingMessage}
          emptyMessage={emptyMessage}
          displayOptions={displayOptions}
          highlightedIndex={highlightedIndex}
          onlyKeyboardSyncsListScroll={onlyKeyboardSyncsListScroll}
          shouldSyncScrollToHighlightRef={shouldSyncScrollToHighlightRef}
          richSubtitlePrimaryClassName={richSubtitlePrimaryClassName}
          richSubtitleSecondaryClassName={richSubtitleSecondaryClassName}
          value={value}
          onSelect={handleSelect}
          onHighlight={setHighlightedIndex}
          optionsContainerRef={optionsContainerRef}
          nativeOptionsWheelScroll={nativeOptionsWheelScroll}
          onWheel={nativeOptionsWheelScroll ? undefined : handleOptionsWheel}
        />
      ) : null}
    </div>
  );
};

export default VirtualizedDropdown;
